import { FC, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
	Close as CloseIcon,
	CloseOutlined as CloseOutlinedIcon,
	Edit as EditIcon,
	Fullscreen as FullscreenIcon,
	OpenInNew as NewWindowIcon,
	Print as PrintIcon,
} from "@mui/icons-material";
import {
	Box,
	Divider,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import rehypeRaw from "rehype-raw";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";
import {
	useFetchChecklist,
	useFetchCommentsByChecklistId,
} from "@/components/Procedures/API/ChecklistQueries";
import { ChecklistNavigationControls } from "@/components/Procedures/Checklist/ChecklistNavigationControls";
import { ChecklistRow } from "@/components/Procedures/Checklist/ChecklistRow";
import {
	buildStepWithActiveListenerTags,
	validSchemeId,
} from "@/components/Procedures/Checklist/utils/Helpers";
import {
	HeaderTools,
	IActiveProcedureChecklist,
	IActiveProcedureChecklistWithEdit,
	IChecklistStep,
	IProceduresSingleChecklist,
} from "@/components/Procedures/Checklist/utils/Types";
import { checklistProcedureType } from "@/components/Procedures/Pages/ProcedurePage";

import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { ProcedureListenerTag } from "@/model/Procedure/ProcedureListenerTag";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

import { useClassicMode, useQueryParamHide } from "@/service/Hooks";
import { UppdatedProcedureCheckList } from "@/service/ProcedureMutations";
import {
	useFetchActiveChecklist,
	useFetchActiveChecklistStatus,
	useFetchActiveProcedureDetails,
	useFetchSuggestionsByChecklistId,
	useHandleProcedureDetails,
} from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

/* ────────────────────────────────────────────────────────── */
/* Shell wrapper                                              */
/* ────────────────────────────────────────────────────────── */

interface ChecklistProps {
	hide?: boolean;
}

export const Checklist: FC<ChecklistProps> = ({ hide, children }) => {
	if (hide) return null;

	return (
		<Box
			sx={{
				bgcolor: "background.default",
				borderRadius: 2,
			}}
		>
			{children}
		</Box>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Body                                                       */
/* ────────────────────────────────────────────────────────── */

export const ChecklistBody: FC<IProceduresSingleChecklist> = (props) => {
	const { activeSchemeId, materialHandling, editMode } = props;
	const { id } = useParams<RouteParams>();

	const [activeListenerTags, setActiveListenerTags] =
		useState<ProcedureListenerTag[]>();

	const { isLoading: isLoadingChecklist, data: checklist } =
		useFetchChecklist(activeSchemeId);

	const { isLoading: isLoadingStepStatuses, data: statuses } =
		useFetchActiveChecklistStatus(activeSchemeId);

	const { isLoading: isLoadingComments, data: comments } =
		useFetchCommentsByChecklistId(activeSchemeId);

	const { isLoading: isLoadingSuggestions, data: suggestions } =
		useFetchSuggestionsByChecklistId(activeSchemeId);

	useEffect(() => {
		setActiveListenerTags(checklist?.ActiveListenerTags);
	}, [checklist]);

	if (editMode) return null;

	const isLoading =
		isLoadingChecklist ||
		isLoadingStepStatuses ||
		isLoadingComments ||
		isLoadingSuggestions;

	return (
		<section className="procedure-wrapper">
			<Box className="procedure-wrapper-inner">
				<Box className="grid-wrapper">
					<ManualHeading activeSchemeId={activeSchemeId} />

					{/* Loading state */}
					{isLoading && (
						<Paper
							elevation={0}
							sx={{
								p: 2,
								border: (t) => `1px solid ${t.palette.divider}`,
								borderRadius: 2,
							}}
						>
							{[...Array(4)].map((_, i) => (
								<Box key={i} sx={{ mb: 2 }}>
									<Skeleton variant="text" height={28} width="60%" />
									<Skeleton
										variant="rectangular"
										height={56}
										sx={{ mt: 1, borderRadius: 1 }}
									/>
								</Box>
							))}
						</Paper>
					)}

					{/* Empty state */}
					{!isLoading && (!checklist?.Steps?.length || !checklist) && (
						<Paper
							elevation={0}
							sx={{
								p: 3,
								textAlign: "center",
								border: (t) => `1px dashed ${t.palette.divider}`,
								borderRadius: 2,
								color: "text.secondary",
							}}
						>
							<Typography fontWeight={600}>
								<Translate String="Procedure.Checklist.Empty">
									No steps available
								</Translate>
							</Typography>
							<Typography variant="body2">
								<Translate String="Procedure.Checklist.EmptyHint">
									Add steps to this checklist to get started.
								</Translate>
							</Typography>
						</Paper>
					)}

					{/* Steps */}
					{!isLoading &&
						checklist?.Steps?.map((step) => {
							const stepComments = comments?.[step?.Id] ?? [];
							const stepSuggestions = Array.isArray(suggestions)
								? suggestions.filter((s) => s.StepId === step.Id)
								: [];

							const status = Array.isArray(statuses)
								? statuses.find((x) => x.Id === step.Id)
								: undefined;

							// keep fetched data immutable; build an enriched copy
							const enrichedStep: ProcedureNodeStep = {
								...step,
								Comments: stepComments,
								ProcedureUpdateSuggestions: stepSuggestions,
								IsSkipped: status?.IsSkipped,
								IsOverridden: status?.IsOverridden,
							};

							return (
								<ChecklistRow
									key={step.Id}
									showCommentsAndSuggestion
									activeSchemeId={activeSchemeId}
									materialHandling={materialHandling}
									step={buildStepWithActiveListenerTags(
										enrichedStep,
										activeListenerTags,
									)}
								/>
							);
						})}
				</Box>
			</Box>
		</section>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Section label (“Activity”)                                 */
/* ────────────────────────────────────────────────────────── */

export const ManualHeading: FC<IActiveProcedureChecklist> = ({
	activeSchemeId,
}) => {
	const { data: ProcedureDetails } = useFetchActiveProcedureDetails();
	const { data: checklist } = useFetchActiveChecklist(activeSchemeId, 0, "");

	const { activeChildNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		activeSchemeId,
	);

	if (!activeChildNode?.ImportedChecklist && checklist) {
		return (
			<Box className="grid-row grid-row-heading" sx={{ mb: 1.5 }}>
				<Typography variant="overline" sx={{ letterSpacing: 1.1 }}>
					<Translate String="Procedure.Checklist.ManualHeading">
						Activity
					</Translate>
				</Typography>
			</Box>
		);
	}
	return null;
};

/* ────────────────────────────────────────────────────────── */
/* (Legacy) rich header renderer                              */
/* ────────────────────────────────────────────────────────── */

const isHeader = (step: ProcedureNodeStep) =>
	step.Index === 1 && isMultipleFieldStep(step.NumberOfFields);
const isMultipleFieldStep = (numberOfFields: number) => numberOfFields > 0;

const Header: FC<IChecklistStep> = ({ step, activeSchemeId }) => {
	const classicMode = useClassicMode(activeSchemeId);
	if (classicMode) {
		return (
			<SATOSContentRender rehypePlugins={[rehypeRaw]} content={step.Value} />
		);
	}
	return <SATOSContentRender content={step.Value} />;
};

/* ────────────────────────────────────────────────────────── */
/* Checklist Header (title + ingress)                         */
/* ────────────────────────────────────────────────────────── */

export const ChecklistHeader: FC<IProceduresSingleChecklist> = (props) => {
	const { activeSchemeId, editMode, children } = props;

	const { data: checklist, isLoading } = useFetchActiveChecklist(
		activeSchemeId,
		0,
		"",
	);

	if (isLoading) {
		return (
			<Box className="heading-wrapper" sx={{ mb: 2 }}>
				<Box sx={{ p: 2 }}>
					<Skeleton variant="text" width="60%" height={36} />
					<Skeleton variant="text" width="80%" height={22} />
				</Box>
			</Box>
		);
	}

	if (!checklist) {
		return (
			<Box className="heading-wrapper" sx={{ mb: 2 }}>
				<Typography color="text.secondary">
					<Translate>No checklist data available.</Translate>
				</Typography>
			</Box>
		);
	}

	if (editMode) {
		return <Box className="heading-wrapper">{children}</Box>;
	}

	return (
		<Box className="heading-wrapper" sx={{ mb: 2 }}>
			{children}
			<Box className="heading-wrapper-container">
				<Box className="heading-wrapper-user-imported heading-wrapper-left">
					<Typography variant="h4" className="heading" sx={{ fontWeight: 700 }}>
						{checklist?.Name}
					</Typography>
				</Box>
			</Box>
			{!!checklist?.Ingress && (
				<Box className="heading-wrapper-container" sx={{ mt: 0.5 }}>
					<Box className="heading-wrapper-user-imported">
						<SATOSContentRender content={checklist?.Ingress ?? ""} />
					</Box>
				</Box>
			)}
			<Divider sx={{ mt: 2 }} />
		</Box>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Header toolbar (navigation, edit, print, etc.)             */
/* ────────────────────────────────────────────────────────── */

interface ChecklistHeadingTools extends IActiveProcedureChecklistWithEdit {
	activeTools?: Set<HeaderTools>;
	checklistProcedureType: checklistProcedureType;
	editMode: boolean | undefined;
	setEditMode?: (editMode: boolean) => void;
}

export const ChecklistHeadingTools: FC<ChecklistHeadingTools> = (props) => {
	const { id, schemeId } = useParams<RouteParams>();
	const queryHide = useQueryParamHide();
	const hide = queryHide ? "" : "hide=1";

	const { data: ProcedureDetails } = useFetchActiveProcedureDetails();
	const { data: ProcedureDetailsById } = useFetchChecklist(
		id,
		Number(schemeId) || 0,
	);

	const { activeChildNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		props.activeSchemeId,
	);

	if (!props.activeTools) return null;

	return (
		<Stack
			direction="row"
			spacing={0.5}
			alignItems="center"
			className="heading-wrapper-tools"
			sx={{ mb: 1 }}
		>
			{props.activeTools.has("navigation") && (
				<ChecklistNavigationControls {...props} />
			)}

			{props.activeTools.has("fullscreen") && (
				<Tooltip
					title={
						<Translate String="Checklist.Tools.Fullscreen">
							Fullscreen
						</Translate>
					}
				>
					<IconButton
						component={SATOSLink}
						to="AccessProcedureNode"
						params={[id, props.activeSchemeId]}
						search={hide}
						className="button-icon"
						aria-label="Fullscreen"
					>
						<FullscreenIcon />
					</IconButton>
				</Tooltip>
			)}

			<AdminProtected>
				<ChecklistHeadingToolEdit
					{...props}
					activeChildNode={activeChildNode}
					importedChecklist={
						ProcedureDetailsById?.ImportedChecklist ||
						activeChildNode?.ImportedChecklist
					}
					setEditMode={props.setEditMode!}
				/>
			</AdminProtected>

			{props.activeTools.has("print") && (
				<Tooltip
					title={<Translate String="Checklist.Tools.Print">Print</Translate>}
				>
					<IconButton
						component={SATOSLink}
						to="ChecklistPrint"
						params={[id, props.activeSchemeId]}
						serverSideRendering
						className="button-icon"
						aria-label="Print"
					>
						<PrintIcon />
					</IconButton>
				</Tooltip>
			)}

			{props.activeTools.has("openInNewWindow") && (
				<Tooltip
					title={
						<Translate String="Checklist.Tools.OpenNew">
							Open in new window
						</Translate>
					}
				>
					<IconButton
						component={SATOSLink}
						to="AccessProcedureNode"
						params={[id, props.activeSchemeId]}
						search="hide=1"
						target="_blank"
						className="button-icon"
						aria-label="Open in new window"
					>
						<NewWindowIcon />
					</IconButton>
				</Tooltip>
			)}

			{props.activeTools.has("close") &&
				props.checklistProcedureType === "procedure" && (
					<Tooltip
						title={<Translate String="Checklist.Tools.Close">Close</Translate>}
					>
						<IconButton
							component={SATOSLink}
							to="tremeny0"
							className="button-icon"
							aria-label="Close"
						>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				)}

			{props.activeTools.has("close") &&
				props.checklistProcedureType === "materialhandling" && (
					<Tooltip
						title={<Translate String="Checklist.Tools.Close">Close</Translate>}
					>
						<IconButton
							component={SATOSLink}
							to="MaterialHandlingMyPage"
							className="button-icon"
							aria-label="Close"
						>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				)}
		</Stack>
	);
};

interface ChecklistHeadingToolsLink extends ChecklistHeadingTools {
	activeChildNode: ProcedureDetailNode | undefined;
	importedChecklist: boolean | undefined;
	setEditMode: (editMode: boolean) => void;
}

const ChecklistHeadingToolEdit: FC<ChecklistHeadingToolsLink> = (props) => {
	const [redirect, setRedirect] = useState(false);
	const [procedureId, setProcedureId] = useState<number>(0);
	const [nodeSchemeId, setNodeSchemeId] = useState<number>(0);
	const [errorMessage, setErrorMessage] = useState<string>("");

	const { id } = useParams<RouteParams>();
	const updatedProcedureCheckList = UppdatedProcedureCheckList();
	const navigate = useNavigate();

	const updatePage = async () => {
		const response = await updatedProcedureCheckList(props.activeSchemeId);
		if (response.ProcedureId > 0 && response.NodeSchemeId > 0) {
			setProcedureId(response.ProcedureId);
			setNodeSchemeId(response.NodeSchemeId);
			setRedirect(true);
		} else {
			setErrorMessage(response.ResponseText);
		}
	};

	if (redirect) {
		return (
			<Navigate
				to={`/procedure/${procedureId}/compareUpdates/parent/${nodeSchemeId}`}
			/>
		);
	}

	if (
		!props.activeTools ||
		!props.activeTools.has("edit") ||
		!validSchemeId(props.activeSchemeId)
	) {
		return null;
	}

	if (props.checklistProcedureType === "procedure") {
		return (
			<Tooltip
				title={<Translate String="Checklist.Tools.Edit">Edit</Translate>}
			>
				<IconButton
					onClick={(e) => {
						e.preventDefault();
						navigate(`edit`);
					}}
					className="button-icon"
					aria-label="Edit"
				>
					{props.editMode ? <CloseOutlinedIcon /> : <EditIcon />}
				</IconButton>
			</Tooltip>
		);
	}

	if (props.checklistProcedureType === "materialhandling") {
		return (
			<Tooltip
				title={<Translate String="Checklist.Tools.Edit">Edit</Translate>}
			>
				<IconButton
					component={SATOSLink}
					to="EditMaterialHandling"
					params={[id]}
					className="button-icon"
					aria-label="Edit"
				>
					<EditIcon />
				</IconButton>
			</Tooltip>
		);
	}

	return null;
};
